<template>
	<div v-if="loading">Loading...</div>

	<PurchaseOptions
		v-else-if="selectedOption?.id"
		@goback="handleGoBack"
		@subscribe="handleSuccess"
	/>

	<div v-else-if="diplayModal">
		<div
			v-if="exceedLimit && !showModal"
			class="flex flex-col justify-center text-won-blue"
		>
			<h2 class="mb-4 text-center text-[24px] font-bold">
				{{ featureModalTitle }}
			</h2>
			<p
				class="text-center"
				v-html="errorMessageExceededLimit"
			/>
		</div>
		<div class="mt-4 pb-10">
			<PricingTable
				:show-coaching="showCoachingCard"
				:allow-exit-to-free-plan="allowExitToFreePlan"
				@select="handlePurchase"
				@upgrade-later="handleUpgradeLater"
			/>
		</div>
	</div>
</template>

<script setup>
import { ref, onMounted, computed, toRefs } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { setAuthState } from '../composables/auth';
import { useSubscriptionPlanStore } from '../store/subscription.store';
import { useSubscriptionTrackerStore } from '../store/subscription-tracker.store';
import { useFeatureUsageStore } from '../store/feature-usage.store';
import { usePremiumNudgeStore } from '../store/nudge-premium.store';
import { useOnboardingRetentionStore } from '../store/onboarding-retention.store';
import { FEATURE_EXCEED_TITLE_MAP, shouldShowCoachingCard } from '../utils/subscription';

import PurchaseOptions from './purchase-options.vue';
import PricingTable from './purchase/pricing/pricing-table.vue';

const route = useRoute();

const props = defineProps({
	exceedLimit : {
		type: Boolean,
		default: false,
	},
	showModalToUsers : {
		type: Boolean,
		default: false,
	},
	errorMessageExceededLimit: {
		type: String,
		default: ''
	},
	allowExitToFreePlan: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(['selectedPackage', 'goback', 'subscribe']);

const subscriptionStore = useSubscriptionPlanStore();
const subscriptionTrackerStore = useSubscriptionTrackerStore();
const featureUsagestore = useFeatureUsageStore();
const premiumNudgeStore = usePremiumNudgeStore();
const onboardingRetentionStore = useOnboardingRetentionStore();
const { getUser } = setAuthState();
const { exceedLimit } = toRefs(props);
const { currentFeature } = storeToRefs(featureUsagestore);
const { subscriptionPlans, selectedOption } = storeToRefs(subscriptionStore);
const { showModal } = storeToRefs(premiumNudgeStore);

const success = defineModel();
const loading = ref(false);

const diplayModal = computed(() => (exceedLimit.value || route.name === 'settings') && !success.value);
const featureModalTitle = computed(() => { return FEATURE_EXCEED_TITLE_MAP[currentFeature.value];});
const showCoachingCard = computed(() => shouldShowCoachingCard({
	routeName: route.name,
	exceedLimit: exceedLimit.value,
	user: getUser(),
}));

if (!subscriptionPlans.value?.length) {
	loading.value = true;
	subscriptionStore.initPlans().then(() => (loading.value = false));
}

onMounted(() => {
	if (! document.querySelector('script[src="https://js.stripe.com/v3/"]')) {
		const recaptchaScript = document.createElement('script');
		recaptchaScript.setAttribute('src', 'https://js.stripe.com/v3/');
		document.head.appendChild(recaptchaScript);
	}
});

const handleGoBack = () => {
	subscriptionStore.selectOption();
	subscriptionStore.setCouponCode(null);
	premiumNudgeStore.goBack();
};

const handlePurchase = (cycle) => {
	const { premiumOptions } = subscriptionStore;
	subscriptionStore.selectOption(premiumOptions?.[cycle] ?? premiumOptions?.monthly);
	subscriptionTrackerStore.handleUpgradedButton();
};

// EN-860: declining the AutoApply credits-exhausted paywall offers a free coaching
// call instead, mirroring the "Stay On Free" nudge on the first-application paywall.
const handleUpgradeLater = async () => {
	await subscriptionStore.handleCloseModal();

	if (currentFeature.value === 'auto_apply_v2') {
		onboardingRetentionStore.openCoachingModal();
	}
};

const handleSuccess = async() => {
	subscriptionStore.selectOption();
	subscriptionStore.refresh();
	loading.value = false;
	success.value = true;

	if (props.showModalToUsers) {
		emit('subscribe');
	}
	setTimeout(() => {
		subscriptionStore.handleCloseModal();
		success.value = false;
	},5000);
	await subscriptionTrackerStore.handleSuccessfulPayment();
};
</script>
